import type { Decoration, DecoratedFragment, DecoratedToken } from '../types/decoration';
import type { HighlightedLine, HighlightedToken } from '../types/highlighted-line';

/**
 * Clamp each decoration's range to `[0, lineLength]` and drop any
 * decoration whose effective range collapses to empty. Returns a new
 * array; the input is not mutated.
 */
const clampDecorationsToLine = (
	decorations: readonly Decoration[],
	lineLength: number,
): Decoration[] => {
	const clamped: Decoration[] = [];
	for (const decoration of decorations) {
		const startChar = Math.max(0, decoration.startChar);
		const endChar = Math.min(lineLength, decoration.endChar);
		if (endChar <= startChar) {
			continue;
		}
		clamped.push({ ...decoration, startChar, endChar });
	}
	return clamped;
};

const doesDecorationOverlapRange = (decoration: Decoration, start: number, end: number): boolean =>
	decoration.startChar < end && decoration.endChar > start;

/**
 * Emit one `DecoratedFragment` per contiguous sub-segment of the
 * token, where the segments are defined by adjacent pairs in
 * `sortedPoints`. Each fragment inherits the token's syntax-highlight
 * classes and is styled by every decoration whose range overlaps it.
 *
 * Example:
 *   token.text: "0123456789"
 *   tokenStart: 10
 *   sortedPoints: [10, 13, 15, 16, 18, 20]
 *   decorations:
 *     [
 *       { startChar: 13, endChar: 16, className: 'match' },
 *       { startChar: 15, endChar: 18, className: 'highlight' },
 *     ]
 *   Emitted fragments (relative to tokenStart):
 *     [
 *       { text: "012" },
 *       { text: "34",  className: 'match' },
 *       { text: "5",   className: 'match highlight' },
 *       { text: "67",  className: 'highlight' },
 *       { text: "89" },
 *     ]
 */
const buildTokenFragments = ({
	token,
	tokenStart,
	sortedPoints,
	decorations,
}: {
	token: HighlightedToken;
	tokenStart: number;
	sortedPoints: readonly number[];
	decorations: readonly Decoration[];
}): DecoratedFragment[] => {
	const baseClassName = token.classes?.join(' ');
	const fragments: DecoratedFragment[] = [];

	for (let i = 0; i < sortedPoints.length - 1; i += 1) {
		const segmentStart = sortedPoints[i];
		const segmentEnd = sortedPoints[i + 1];
		const text = token.text.slice(segmentStart - tokenStart, segmentEnd - tokenStart);

		const fragment: DecoratedFragment = { text };
		if (baseClassName) {
			fragment.className = baseClassName;
		}

		for (const decoration of decorations) {
			if (!doesDecorationOverlapRange(decoration, segmentStart, segmentEnd)) {
				continue;
			}
			if (decoration.className) {
				fragment.className = fragment.className
					? `${fragment.className} ${decoration.className}`
					: decoration.className;
			}
			if (decoration.style) {
				fragment.style = { ...fragment.style, ...decoration.style };
			}
			if (decoration.tagName) {
				// Destructuring is more awkward here than direct access is
				// eslint-disable-next-line prefer-destructuring
				fragment.tagName = decoration.tagName;
			}
		}

		fragments.push(fragment);
	}

	return fragments;
};

/**
 * Inputs to `applyDecorations`. The line is the syntax-highlighted
 * tokens for one line; `decorations` are the decorations that target
 * this line (already filtered by `lineNumber`). Pre-filtering is the
 * caller's responsibility — the merger does not re-check `lineNumber`.
 *
 * `decorations` is optional. When omitted, the merger emits one
 * fragment per syntax-highlight token with no decoration styling
 * applied.
 */
export type ApplyDecorationsInput = {
	line: HighlightedLine;
	decorations?: readonly Decoration[];
};

/**
 * Split each syntax-highlight token into the minimal set of contiguous
 * fragments needed to apply the supplied decorations, returning a flat
 * array of `DecoratedToken`s.
 *
 * The output preserves the original line text exactly: concatenating
 * the `text` of every returned node reconstructs the input line.
 *
 * Decorations whose effective range (after clamping to the line's
 * length) is empty are silently skipped. When multiple decorations
 * overlap the same fragment:
 *
 * - `className` strings are unioned (space-separated).
 * - `style` properties are merged shallowly with later decorations
 *   winning on conflict.
 * - `tagName` follows last-wins semantics.
 */
export const applyDecorations = ({
	line,
	decorations = [],
}: ApplyDecorationsInput): DecoratedToken[] => {
	const lineLength = line.reduce((sum, token) => sum + token.text.length, 0);
	const clampedDecorations = clampDecorationsToLine(decorations, lineLength);

	const nodes: DecoratedFragment[] = [];
	let charOffset = 0;

	for (const token of line) {
		if (token.text.length === 0) {
			continue;
		}

		const tokenStart = charOffset;
		const tokenEnd = charOffset + token.text.length;
		charOffset = tokenEnd;

		/*
		 * Collect the split points within this token: the inner edges of
		 * every decoration whose range overlaps the token. Combined with
		 * the token's own boundaries (`tokenStart`, `tokenEnd`), these
		 * define the contiguous sub-segments to emit.
		 */
		const splitPoints = new Set<number>([tokenStart, tokenEnd]);

		for (const decoration of clampedDecorations) {
			/**
			 * A decoration might:
			 * - Not overlap the token at all (don't add split points)
			 * - Partially overlap a token by starting outside and ending inside (add a split point at the decoration end character)
			 * - Partially overlap a token by starting inside and ending outside (add a split point at the decoration start character)
			 * - Fit entirely within a token by starting *and* ending inside(add split points at both decoration characters)
			 * - Completely overlap a token (don't add split points)
			 */
			if (!doesDecorationOverlapRange(decoration, tokenStart, tokenEnd)) {
				continue;
			}
			// The decoration begins inside of the token
			if (decoration.startChar > tokenStart) {
				splitPoints.add(decoration.startChar);
			}
			// The decoration ends inside of the token
			if (decoration.endChar < tokenEnd) {
				splitPoints.add(decoration.endChar);
			}
		}

		const sortedPoints = Array.from(splitPoints).sort((a, b) => a - b);

		nodes.push(
			...buildTokenFragments({
				token,
				tokenStart,
				sortedPoints,
				decorations: clampedDecorations,
			}),
		);
	}

	return nodes;
};
